// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { bootstrapActionTypes, featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// actions
import { actionCreators as offerReduxActionCreators, actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import * as firstPencilActionCreators from '../actions/firstPencilActionCreators';
import * as dealTemplateActionCreators from '../actions/dealTemplateActionCreators';
import * as programActionCreators from '../actions/programActionCreators';

// action types
import { FIRST_PENCIL_APPLY_SUCCEEDED, FIRST_PENCIL_CREATE_CONFIRMED } from '../actionTypes/firstPencilActionTypes';
import { MANUALLY_APPLY_DEAL_TEMPLATE } from '../actionTypes/dealTemplateActionTypes';
import { OFFER_SAVE_FAILURE } from '../actionTypes/offerActionTypes';
import { PAYMENT_FAILURE, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';

// types
import type { FirstPencilIncentiveOverride, FirstPencilManualIncentive } from '../types/firstPencilTypes';

// utils
import { isApplicableManualIncentive } from '../utils/firstPencilUtils';
import { logFirstPencilAutoApplyTemplateBypass, logFirstPencilDealLaunched } from '../utils/firstPencilTelemetry';
import { getOriginalActionType } from '../utils/paymentActionUtils';

// selectors
import * as firstPencilSelectors from '../selectors/firstPencilSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

// A set, not a slot: open at most once per session/deal, but don't let A→B→A reopen a dismissed screen.
const handledSessionIds = new Set<string>();

// Comparison/scenario baggage only loads on BOOTSTRAP_SUCCESS; INIT_SUCCESS can land first with it empty,
// which the gate misreads as unworked. Only open once this session's bootstrap baggage has landed.
const bootstrappedSessionIds = new Set<string>();

/**
 * Telemetry-only dedupe, keyed by dealXgId (not sessionId, which is regenerated on every bootstrap).
 * A real create's own post-apply reload sees the now-true template/program marker and would otherwise
 * misreport it as an independent auto-apply bypass — this does not gate the modal itself.
 */
const bypassLoggedDealXgIds = new Set<string>();

/**
 * F280192 telemetry: deals actually created via First Pencil, keyed by dealXgId (not sessionId, which
 * is regenerated on the post-apply reload). Read by paymentMiddleware's structure-change telemetry,
 * which must not key on the durable template-applied / auto-select-program-saved markers — those are
 * true for plenty of deals First Pencil never touched.
 */
const createdViaFirstPencilDealXgIds = new Set<string>();

export const hasCreatedViaFirstPencil = (dealXgId: string | undefined): boolean =>
    Boolean(dealXgId) && createdViaFirstPencilDealXgIds.has(dealXgId as string);

export const firstPencilInterceptMiddlewareClearCache = (): void => {
    handledSessionIds.clear();
    bootstrappedSessionIds.clear();
    bypassLoggedDealXgIds.clear();
    createdViaFirstPencilDealXgIds.clear();
};

const openIfNeeded = (store: ReduxStore<StateTree>, knownSessionId?: string): void => {
    const state = store.getState();

    // Toggle selectors throw pre-bootstrap, and INIT_SUCCESS can land first.
    if (!featureToggleSelectors.areFeatureTogglesAvailable(state)) {
        return;
    }

    if (!firstPencilSelectors.isFirstPencilLaunch(state)) {
        const bypassReason = firstPencilSelectors.getAutoApplyBypassReason(state);
        const dealXgId = firstPencilSelectors.getLaunchDealXgId(state);
        if (bypassReason && dealXgId && !bypassLoggedDealXgIds.has(dealXgId)) {
            bypassLoggedDealXgIds.add(dealXgId);
            logFirstPencilAutoApplyTemplateBypass(bypassReason);
        }
        return;
    }

    const sessionId = knownSessionId ?? offerReduxSelectors.getSessionId(state);
    if (!sessionId || handledSessionIds.has(sessionId)) {
        return;
    }

    if (!bootstrappedSessionIds.has(sessionId)) {
        return;
    }

    handledSessionIds.add(sessionId);
    const dealXgId = firstPencilSelectors.getLaunchDealXgId(state);
    if (dealXgId) {
        bypassLoggedDealXgIds.add(dealXgId);
    }
    logFirstPencilDealLaunched();
    store.dispatch(firstPencilActionCreators.interceptOpened(firstPencilSelectors.getSciRequestCommentAtLoad(state)));
};

// Vehicle is switched with payment off so the template apply is the single calculation the dealer sees.
const switchToSelectedVehicle = (store: ReduxStore<StateTree>, state: StateTree): void => {
    const selectedVehicle = firstPencilSelectors.getSelectedVehicle(state);
    if (selectedVehicle && selectedVehicle.vin !== vehicleSelectors.getVehicleVin(state)) {
        store.dispatch(offerReduxActionCreators.switchVehicle(selectedVehicle, false));
    }
};

const applyTemplateSelection = (store: ReduxStore<StateTree>): void => {
    const state = store.getState();

    const templateId = firstPencilSelectors.getSelectedTemplateId(state);
    if (!templateId) {
        store.dispatch(firstPencilActionCreators.applySucceeded());
        return;
    }

    switchToSelectedVehicle(store, state);

    const templateName = firstPencilSelectors.getSelectedTemplateName(state);
    store.dispatch(dealTemplateActionCreators.manuallyApplyDealTemplate(templateId, templateName || '', 'firstPencil'));
};

const toManualIncentiveEntry = ({ editId, program, name, code, amount, type }: FirstPencilManualIncentive) => ({
    _editId: editId,
    program,
    name: name.trim(),
    code,
    amount,
    originalAmount: amount,
    type,
    description: '',
    manual: true,
    isStandardIncentive: false,
    isNewIncentive: true,
    deleted: false
});

const applyManualIncentives = (store: ReduxStore<StateTree>, manualIncentives: FirstPencilManualIncentive[]): void => {
    manualIncentives
        .filter(isApplicableManualIncentive)
        .forEach((incentive) =>
            store.dispatch(offerReduxActionCreators.modifyManualIncentive(toManualIncentiveEntry(incentive), 'add', false))
        );
};

const isValidOverride = (override: FirstPencilIncentiveOverride): boolean => Boolean(override.name.trim()) && override.amount > 0;

const isConvertingOverride = (override: FirstPencilIncentiveOverride): boolean =>
    isValidOverride(override) && override.amount !== override.originalAmount;

const isLabelOverride = (override: FirstPencilIncentiveOverride): boolean =>
    isValidOverride(override) &&
    override.amount === override.originalAmount &&
    (override.name.trim() !== override.programName || override.program !== override.programId);

const toOverrideEntry = ({ incentiveId, program, name, amount, incentiveType, programName }: FirstPencilIncentiveOverride) => ({
    _editId: `first-pencil-override-${incentiveId}`,
    program,
    name: name.trim(),
    code: incentiveId,
    amount,
    originalAmount: amount,
    type: incentiveType,
    description: programName,
    manual: true,
    isStandardIncentive: false,
    isNewIncentive: true,
    deleted: false
});

const withoutOverridden = (ids: number[], overrides: FirstPencilIncentiveOverride[]): number[] =>
    ids.filter((id) => !overrides.some((override) => override.incentiveId === id));

const applyProgramSelection = (store: ReduxStore<StateTree>): void => {
    const state = store.getState();

    const selectedProgram = firstPencilSelectors.getSelectedProgram(state);
    if (!selectedProgram) {
        store.dispatch(firstPencilActionCreators.applySucceeded());
        return;
    }

    if (selectedProgram.offerType !== offerReduxSelectors.getCurrentOfferType(state)) {
        store.dispatch(
            offerReduxActionCreators.updatedOfferType(selectedProgram.offerType, { payment: false, suppressAnalytics: true })
        );
    }

    switchToSelectedVehicle(store, state);

    applyManualIncentives(store, firstPencilSelectors.getManualIncentives(state));

    const { creditScoreOverride } = selectedProgram.program;
    if (creditScoreOverride != null) {
        store.dispatch(programActionCreators.setCreditScoreOverride(creditScoreOverride));
    }

    const selectedIncentives = firstPencilSelectors.getSelectedIncentives(state);
    const overrides = firstPencilSelectors.getSelectedIncentiveOverrides(state).filter(isConvertingOverride);
    overrides.forEach((override) =>
        store.dispatch(offerReduxActionCreators.modifyManualIncentive(toOverrideEntry(override), 'add', false))
    );
    store.dispatch(
        programActionCreators.applySelectedProgram(
            selectedProgram.program,
            selectedIncentives
                ? {
                      forceReapply: true,
                      explicitConditionalIds: withoutOverridden(selectedIncentives.conditionalIds, overrides),
                      explicitNonConditionalIds: withoutOverridden(selectedIncentives.nonConditionalIds, overrides),
                      standardsSelectionEstablished: true,
                      manualProgramSelect: true
                  }
                : { forceReapply: true, manualProgramSelect: true }
        )
    );
};

const applyLabelOverrides = (store: ReduxStore<StateTree>): void => {
    const state = store.getState();
    const appliedIncentives = offerReduxSelectors.getManualIncentives(state) ?? [];
    const edits = firstPencilSelectors
        .getSelectedIncentiveOverrides(state)
        .filter(isLabelOverride)
        .map((override) => {
            const applied = appliedIncentives.find((incentive) => String(incentive.code) === String(override.incentiveId));
            return applied && { ...applied, program: override.program, name: override.name.trim(), manual: false };
        })
        .filter(Boolean);
    edits.forEach((edit, index) =>
        store.dispatch(programActionCreators.modifyManualIncentiveCompactUI(edit, 'edit', index === edits.length - 1))
    );
};

const isApplyingPrograms = (state: StateTree): boolean =>
    firstPencilSelectors.isApplyingSelection(state) && firstPencilSelectors.getSelectedSetupMode(state) === 'programs';

const isApplyingTemplate = (state: StateTree): boolean =>
    firstPencilSelectors.isApplyingSelection(state) && firstPencilSelectors.getSelectedSetupMode(state) === 'template';

// Only the program's own payment settles it: a Lease switch can send an endpoint payment ahead of it.
const isProgramApplyResult = (state: StateTree, action: AnyFSA): boolean =>
    isApplyingPrograms(state) && getOriginalActionType(action) === offerReduxActionTypes.APPLIED_PROGRAM;

const applySelectionOnCreate = (store: ReduxStore<StateTree>): void => {
    switch (firstPencilSelectors.getSelectedSetupMode(store.getState())) {
        case 'template':
            applyTemplateSelection(store);
            break;
        case 'programs':
            applyProgramSelection(store);
            break;
        default:
            store.dispatch(firstPencilActionCreators.applySucceeded());
            break;
    }
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    // Reducers first, so state is settled before we read it.
    next(action);

    switch (action.type) {
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            // next() ran the reducers, so the baggage is in state now — mark the session hydrated.
            const sessionId = offerReduxSelectors.getSessionId(store.getState());
            if (sessionId) {
                bootstrappedSessionIds.add(sessionId);
            }
            openIfNeeded(store, sessionId);
            if (isApplyingTemplate(store.getState())) {
                store.dispatch(firstPencilActionCreators.applySucceeded());
            }
            break;
        }
        case OFFER_SAVE_FAILURE: {
            if (getOriginalActionType(action) === MANUALLY_APPLY_DEAL_TEMPLATE && isApplyingTemplate(store.getState())) {
                store.dispatch(firstPencilActionCreators.applyFailed());
            }
            break;
        }
        case PAYMENT_RECEIVED: {
            if (isProgramApplyResult(store.getState(), action)) {
                applyLabelOverrides(store);
                store.dispatch(firstPencilActionCreators.applySucceeded());
            }
            break;
        }
        case PAYMENT_FAILURE: {
            if (isProgramApplyResult(store.getState(), action)) {
                store.dispatch(firstPencilActionCreators.applyFailed());
            }
            break;
        }
        case initActionTypes.INIT_SUCCESS: {
            openIfNeeded(store);
            break;
        }
        case FIRST_PENCIL_CREATE_CONFIRMED: {
            applySelectionOnCreate(store);
            break;
        }
        case FIRST_PENCIL_APPLY_SUCCEEDED: {
            const dealXgId = firstPencilSelectors.getLaunchDealXgId(store.getState());
            if (dealXgId) {
                createdViaFirstPencilDealXgIds.add(dealXgId);
            }
            break;
        }
        default:
            break;
    }
};
